// @ts-strict-ignore
import {AfterViewInit, Component, ElementRef, Injector, ViewChild} from "@angular/core";
import {FormControl, FormGroup, Validators} from "@angular/forms";
import {AudioMeta, FontAwesomeCode, Media, MediaType} from "@models";
import {TranslateService} from "@ngx-translate/core";
import {SimMaterialDialogService, SimMediasetService, NotificationService, SimUtilityExtendedService} from "@services";
import {Translations} from "@translations";
import {
	BasicMaterialDialog,
	MatDialogDataBaseInput,
	MatDialogDataBaseOutput,
} from "../../basic-material-dialog/basic-material-dialog.directive";

export interface DialogData_EditMediaDialogInput extends MatDialogDataBaseInput {
	media: Media;
}

export interface DialogData_EditMediaDialogOutput extends MatDialogDataBaseOutput {
	updatedMedia: Media;
}

@Component({
	selector: "sim-edit-media-dialog",
	templateUrl: "./edit-media-dialog.component.html",
	styleUrls: ["./edit-media-dialog.component.scss"],
	standalone: false,
})
export class EditMediaDialogComponent
	extends BasicMaterialDialog<EditMediaDialogComponent, DialogData_EditMediaDialogInput, DialogData_EditMediaDialogOutput>
	implements AfterViewInit
{
	T = Translations;
	mediaTypeKey: string;

	MediaType = MediaType;
	mediaInformationFormGroup = new FormGroup({
		fileName: new FormControl("", [Validators.required]),
		filePath: new FormControl({value: "", disabled: true}),
		recVolume: new FormControl(0),
		roomVolume: new FormControl(0),
	});

	@ViewChild("audioPlayer") audioPlayerRef: ElementRef<HTMLAudioElement>;
	public mediaIsPlaying: boolean;
	public currentProgress: number = 0;
	public duration: number;
	public durationString: string;
	public currentTime: string = "00:00";
	numberOfPdfPages: number = 1;
	pdfPageNum: number = 1;
	numberOfPowerPointPages: number = 1;
	powerPointPageNum: number = 0;
	powerPointLoaded: boolean;
	pptxLoadingError: boolean;
	pdfLoaded: boolean;
	pdfLoadingError: boolean;
	videoError: boolean;
	audioError: boolean;
	audioLoaded: boolean;
	imageLoadError: boolean;

	constructor(
		public simUtilityService: SimUtilityExtendedService,
		private _simMediaService: SimMediasetService,
		private _simNotificationService: NotificationService,
		private _simMaterialDialogService: SimMaterialDialogService,
		private _translate: TranslateService,
		_injector: Injector,
	) {
		super(_injector);

		this.mediaInformationFormGroup.patchValue({
			fileName: this.data?.media?.name,
			filePath: this.data?.media?.fileName,
		});

		if (this.data?.media?.mediaType === MediaType.Audio && this.data?.media?.metaData?.length > 0) {
			let metaData = <AudioMeta>this.data?.media?.metaData[0];
			this.mediaInformationFormGroup.patchValue({
				roomVolume: metaData.volume,
			});
		}
	}

	ngAfterViewInit() {
		switch (this.data?.media.mediaType) {
			case MediaType.Audio:
				const audioUrl = this.simUtilityService.getMediaPath(this.data.media);
				this.audioPlayerRef.nativeElement.src = audioUrl;
				this.mediaTypeKey = this.T.MEDIA_DIALOG_AUDIO;
				break;
			case MediaType.Video:
				this.mediaTypeKey = this.T.MEDIA_DIALOG_VIDEO;
				break;
			case MediaType.Image:
				this.mediaTypeKey = this.T.MEDIA_DIALOG_IMAGE;
				break;
			case MediaType.PDF:
				this.mediaTypeKey = this.T.MEDIA_DIALOG_PDF;
				break;
			case MediaType.PowerPoint:
				this.mediaTypeKey = this.T.MEDIA_DIALOG_POWERPOINT;
				break;
		}
	}

	playAudio() {
		this.audioPlayerRef.nativeElement.play().then(
			() => {
				this.mediaIsPlaying = true;
			},
			(err) => {
				this._simNotificationService.notifyError(this._translate.instant(Translations.EDIT_MEDIA_DIALOG_NOTIFYERROR));
				this.mediaIsPlaying = false;
			},
		);
	}

	secondsToString(seconds, duration = true) {
		const numminutes = Math.floor((((seconds % 31536000) % 86400) % 3600) / 60);
		const numseconds = Math.round((((seconds % 31536000) % 86400) % 3600) % 60);

		let minutesStr = "";
		let secondsStr = "";

		numminutes <= 9 ? (minutesStr = "0" + numminutes) : (minutesStr = numminutes + "");
		numseconds <= 9 ? (secondsStr = "0" + numseconds) : (secondsStr = numseconds + "");
		if (duration) {
			return minutesStr + " min " + secondsStr + " sec";
		} else {
			return minutesStr + ":" + secondsStr;
		}
	}

	stopAudio() {
		this.audioPlayerRef.nativeElement.pause();
		this.mediaIsPlaying = false;
	}

	timeUpdateAudio($event: Event) {
		const progress = (this.audioPlayerRef.nativeElement.currentTime / this.audioPlayerRef.nativeElement.duration) * 100;
		this.currentProgress = progress;
		this.currentTime = this.secondsToString(this.audioPlayerRef.nativeElement.currentTime, false);
		if (progress === 100) {
			this.mediaIsPlaying = false;
			this.currentProgress = 0;
			this.currentTime = "00:00";
			this.audioPlayerRef.nativeElement.currentTime = 0;
		}
	}

	toggleMediaPlaying() {
		if (this.mediaIsPlaying) {
			this.stopAudio();
		} else {
			this.playAudio();
		}
	}

	audioMetaDataLoaded() {
		this.duration = this.audioPlayerRef.nativeElement.duration;
		this.durationString = this.secondsToString(this.duration);
	}

	enlargeImage() {
		this._simMaterialDialogService.openImageDialog({
			type: "media",
			media: this.data?.media,
		});
	}

	openPdfDialog() {
		this._simMaterialDialogService.openPDFDialog({media: this.data?.media});
	}

	openPowerPointDialog() {
		this._simMaterialDialogService.openPowerPointDialog({
			media: this.data?.media,
		});
	}

	update() {
		this.data.media.name = this.mediaInformationFormGroup.get("fileName").value;
		if (this.data?.media?.mediaType === MediaType.Audio && this.data?.media?.metaData?.length > 0) {
			let metaData = <AudioMeta>this.data?.media?.metaData[0];
			metaData.volume = this.mediaInformationFormGroup.get("roomVolume").value;
		}
		this.close({
			action: "ok",
			updatedMedia: this.data?.media,
		});
	}

	getHeaderIcon(): FontAwesomeCode {
		let result: FontAwesomeCode;
		switch (this.data.media?.mediaType) {
			case MediaType.Audio:
				result = {fontIcon: "fa-volume-up", fontSet: "fad"};
				break;
			case MediaType.PDF:
				result = {fontIcon: "fa-file-pdf", fontSet: "fad"};
				break;
			case MediaType.Excel:
				result = {fontIcon: "fa-file-excel", fontSet: "fad"};
				break;
			case MediaType.Image:
				result = {fontIcon: "fa-image-polaroid", fontSet: "fad"};
				break;
			case MediaType.PowerPoint:
				result = {fontIcon: "fa-file-powerpoint", fontSet: "fad"};
				break;
			case MediaType.Video:
				result = {fontIcon: "fa-file-video", fontSet: "fad"};
				break;
			default:
				result = {fontIcon: "fa-file-editf", fontSet: "fad"};
		}
		return result;
	}
}
